import { CreditCardOutlined, DeleteOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, message, Popconfirm, Spin } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { changeCard, deleteCard, getCards } from "../api";
import { ApiCardsBody } from "../types";
import styles from "./index.module.scss";

export const CardList = () => {
  const queryClient = useQueryClient();
  const [messageApi, contextHolder] = message.useMessage();
  const { t } = useTranslation();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["cards"],
    queryFn: () => getCards(),
  });

  const { mutate: deleteCardMutation, isPending: isDeleteCardPending } =
    useMutation({
      mutationFn: (id: string) => deleteCard(id),
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: ["cards"] });
        messageApi["success"]("The card has been deleted");
      },
      onError(error) {
        messageApi["error"](
          (error as any)?.response?.data?.message ||
            "An error occurred while delete the card",
        );
      },
    });

  const { mutate: changeCardMutation, isPending: isChangeCardPending } =
    useMutation({
      mutationFn: (props: { id: string; body: ApiCardsBody }) =>
        changeCard(props.id, props.body),
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: ["cards"] });
        messageApi["success"]("The card status has been changed");
      },
      onError(error) {
        messageApi["error"](
          (error as any)?.response?.data?.message ||
            "An error occurred while delete the card",
        );
      },
    });

  useEffect(() => {
    if (isError) {
      messageApi.error(
        (error as any)?.response?.data?.message ||
          "An error occurred while receiving the list of cards",
      );
    }
  }, [isError, error, messageApi]);

  if (isLoading || isDeleteCardPending || isChangeCardPending) {
    return (
      <div className={styles.listWrapperLoader}>
        {contextHolder}
        <Spin />
      </div>
    );
  }

  if (!data?.data.length) {
    return (
      <div className={styles.block}>
        {contextHolder}
        <div className={styles.cardIcon}>
          <CreditCardOutlined />
        </div>
        <div className={styles.title}>{t("cards.no_cards")}</div>
      </div>
    );
  }

  return (
    <div>
      {contextHolder}
      {data.data.map((item) => (
        <div className={styles.card} key={item.id}>
          <div className={styles.IconLeftWrapper}>
            <CreditCardOutlined />
          </div>
          <div className={styles.body}>
            <div className={styles.numberTitle}>{t("cards.card_number")}</div>
            <div className={styles.number}>{item.pan_masked}</div>
            {item.status === "ACTIVE" ? (
              <div className={styles.activeText}>{t("cards.active_card")}</div>
            ) : (
              <Popconfirm
                title={t("cards.change_card_title")}
                description={t("cards.change_card_description")}
                onConfirm={() =>
                  changeCardMutation({
                    id: item.id,
                    body: {
                      status: item.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
                    },
                  })
                }
                okText={t("cards.yes")}
                cancelText={t("cards.no")}
                icon={false}
                placement="bottom"
              >
                <Button type="primary">
                  {t("cards.inactive_card_btn_text")}
                </Button>
              </Popconfirm>
            )}
          </div>
          <Popconfirm
            title={t("cards.delete_card_title")}
            description={t("cards.delete_card_description")}
            onConfirm={() => deleteCardMutation(item.id)}
            okText={t("cards.yes")}
            cancelText={t("cards.no")}
            icon={false}
            placement="leftBottom"
          >
            <div className={styles.IconRightWrapper}>
              <DeleteOutlined />
            </div>
          </Popconfirm>
        </div>
      ))}
    </div>
  );
};
